<template>
  <div id="inventoryOutOfStockReport">
    <div class="container">

      <TipBox info :title="$t('inventory_out_of_stock_report.title')">
        <span v-html="$t('inventory_out_of_stock_report.tip')"></span>
      </TipBox>

      <Tabs>
        <TabSection :title="$t('inventory_out_of_stock_report.inventory_status')" v-if="ready">

          <div class="group">
            <label>{{ $t('inventory_out_of_stock_report.search_company') }}</label>
            <input type="text" v-model="companyFilter" :placeholder="$t('search') + '...'">
          </div>

          <Panel collapsible :title="$t('inventory.items')">

            <div class="filter-row">
              <div class="group" v-if="outOfStockItems.length > 0 || itemFilter">
                <label>{{ $t('inventory_out_of_stock_report.search_product') }}</label>
                <input type="text" v-model="itemFilter" :placeholder="$t('search') + '...'">
              </div>
              <div class="action" @click="downloadItems">
                <button class="btn btn-primary">
                  {{ $t('csv_download') }}
                </button>
              </div>
            </div>

            <table v-if="outOfStockItems.length > 0">
              <thead>
                <tr>
                  <th class="name">{{ $t('name') }}</th>
                  <th class="availability">{{ $t('disponibility') }}</th>
                  <th class="companies">{{ $t('companies.title') }}</th>
                  <th class="count">{{ $t('total') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item of outOfStockItems" :key="'item' + item.id">
                  <td class="name">
                    <router-link :to="$router.buildContext('/item/edit', { item: item.id })" target="_blank">
                      {{ $to(item.name) }}
                    </router-link>
                  </td>
                  <td class="availability">{{ $t('items.out_of_stock') }}</td>
                  <td class="companies">
                    <tag grey class="company" v-for="(company, i) of item.companies" :key="'tag-i-' + i">
                      {{ company.name }}
                    </tag>
                  </td>
                  <td class="count">{{ item.companies.length }}</td>
                </tr>
              </tbody>
            </table>
            <i v-if="outOfStockItems.length === 0">
              {{ $t('inventory_out_of_stock_report.empty') }}
            </i>
          </Panel>

          <Panel collapsible :title="$t('items.variants')">
            <div class="filter-row">
              <div class="group" v-if="outOfStockVariants.length > 0 || variantFilter">
                <label>{{ $t('inventory_out_of_stock_report.search_variant') }}</label>
                <input type="text" v-model="variantFilter" :placeholder="$t('search') + '...'">
              </div>
              <div class="action">
                <button class="btn btn-primary" @click="downloadVariants">
                  {{ $t('csv_download') }}
                </button>
              </div>
            </div>

            <table v-if="outOfStockVariants.length > 0">
              <thead>
                <tr>
                  <th class="name">{{ $t('name') }}</th>
                  <th class="availability">{{ $t('disponibility') }}</th>
                  <th class="companies">{{ $t('companies.title') }}</th>
                  <th class="count">{{ $t('total') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="variant of outOfStockVariants" :key="'variant' + variant.id">
                  <td class="name">
                    <router-link :to="$router.buildContext('/item/edit', { item: variant.parentItem.id })"
                      target="_blank">
                      <template v-if="variant.parentItem">{{ $to(variant.parentItem.name) }} - </template>
                      <i>{{ $to(variant.name) }}</i>
                    </router-link>
                  </td>
                  <td class="availability">{{ $t('items.out_of_stock') }}</td>
                  <td class="companies">
                    <tag grey class="company" v-for="(company, i) of variant.companies" :key="'tag-' + i">
                      {{ company.name }}
                    </tag>
                  </td>
                  <td class="count">{{ variant.companies.length }}</td>
                </tr>
              </tbody>
            </table>
            <i v-if="outOfStockVariants.length === 0">
              {{ $t('inventory_out_of_stock_report.empty') }}
            </i>
          </Panel>

          <Panel collapsible :title="$t('inventory.modifiers')">
            <div class="filter-row">
              <div class="group" v-if="outOfStockModifiers.length > 0 || modifierFilter">
                <label>{{ $t('inventory_out_of_stock_report.search_modifier') }}</label>
                <input type="text" v-model="modifierFilter" :placeholder="$t('search') + '...'">
              </div>
              <div class="action">
                <button class="btn btn-primary" @click="downloadModifiers">
                  {{ $t('csv_download') }}
                </button>
              </div>
            </div>

            <table v-if="outOfStockModifiers.length > 0">
              <thead>
                <tr>
                  <th class="name">{{ $t('name') }}</th>
                  <th class="availability">{{ $t('disponibility') }}</th>
                  <th class="companies">{{ $t('companies.title') }}</th>
                  <th class="count">{{ $t('total') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="modifier of outOfStockModifiers" :key="'modifier' + modifier.id">
                  <td class="name">
                    <router-link :to="$router.buildContext('/modifiers/edit', { item: modifier.id })" target="_blank">
                      {{ $to(modifier.name) }}
                    </router-link>
                  </td>
                  <td class="availability">{{ $t('items.out_of_stock') }}</td>
                  <td class="companies">
                    <tag grey class="company" v-for="(company, i) of modifier.companies" :key="'tag-m' + i">
                      {{ company.name }}
                    </tag>
                  </td>
                  <td class="count">{{ modifier.companies.length }}</td>
                </tr>
              </tbody>
            </table>
            <i v-if="outOfStockModifiers.length === 0">
              {{ $t('inventory_out_of_stock_report.empty') }}
            </i>
          </Panel>

          <Panel collapsible :title="$t('inventory.choice_items')">
            <div class="filter-row">
              <div class="group" v-if="outOfStockChoiceItems.length > 0 || choiceItemFilter">
                <label>{{ $t('inventory_out_of_stock_report.search_choice_item') }}</label>
                <input type="text" v-model="choiceItemFilter" :placeholder="$t('search') + '...'">
              </div>
              <div class="action" @click="downloadChoiceItems">
                <button class="btn btn-primary">
                  {{ $t('csv_download') }}
                </button>
              </div>
            </div>

            <table v-if="outOfStockChoiceItems.length > 0">
              <thead>
                <tr>
                  <th class="name">{{ $t('name') }}</th>
                  <th class="availability">{{ $t('disponibility') }}</th>
                  <th class="companies">{{ $t('companies.title') }}</th>
                  <th class="count">{{ $t('total') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="choiceItem of outOfStockChoiceItems" :key="'choiceItem' + choiceItem.id">
                  <td class="name">
                    <router-link :to="$router.buildContext('/choice-items/edit', { item: choiceItem.id })"
                      target="_blank">
                      {{ $to(choiceItem.name) }}
                    </router-link>
                  </td>
                  <td class="availability">{{ $t('items.out_of_stock') }}</td>
                  <td class="companies">
                    <tag grey class="company" v-for="(company, i) of choiceItem.companies" :key="'tag-c' + i">
                      {{ company.name }}
                    </tag>
                  </td>
                  <td class="count">{{ choiceItem.companies.length }}</td>
                </tr>
              </tbody>
            </table>
            <i v-if="outOfStockChoiceItems.length === 0">
              {{ $t('inventory_out_of_stock_report.empty') }}
            </i>
          </Panel>

        </TabSection>
      </Tabs>

    </div>
  </div>
</template>

<script>
  import { Core } from '@/core/Core';

  export default {
    name: "inventoryOutOfStockReport",

    data() {
      return {
        inventory: null,
        companies: [],
        choiceItemFilter: "",
        itemFilter: "",
        modifierFilter: "",
        variantFilter: "",
        companyFilter: "",
        ready: false
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "inventory_out_of_stock_report.title";
      this.$store.state.pageIcon = "/icon/stats.svg";

      showSpinner();
      this.inventory = await this.$store.getters.getContextInventory;
      await this.loadCompanies();
      await this.loadCompanyInventories();
      this.ready = true;
      hideSpinner();
    },

    computed: {
      branchId() {
        return this.$route.query.branch;
      },
      branch() {
        return this.$store.state.contextBranch;
      },
      filteredCompanies() {
        return this.companies.filter(c => c.name.toLowerCase().indexOf(this.companyFilter.toLowerCase()) > -1);
      },
      outOfStockItems() {
        let items = [];
        for (let company of this.filteredCompanies) {
          let outOfStockItems = company.outOfStockItems
            .filter(i => this.$to(i.name).toLowerCase().indexOf(this.itemFilter.toLowerCase()) > -1);
          for (let item of outOfStockItems) {
            let foundItem = items.find(i => i.id === item.id);
            if (!foundItem) {
              item.companies = [company];
              items.push(item);
            } else {
              foundItem.companies.push(company);
            }
          }
        }
        return items;
      },
      outOfStockModifiers() {
        let modifiers = [];
        for (let company of this.filteredCompanies) {
          let outOfStockModifiers = company.outOfStockModifiers
            .filter(m => this.$to(m.name).toLowerCase().indexOf(this.modifierFilter.toLowerCase()) > -1);
          for (let modifier of outOfStockModifiers) {
            let foundModifier = modifiers.find(i => i.id === modifier.id);
            if (!foundModifier) {
              modifier.companies = [company];
              modifiers.push(modifier);
            } else {
              foundModifier.companies.push(company);
            }
          }
        }
        return modifiers;
      },
      outOfStockChoiceItems() {
        let choiceItems = [];
        for (let company of this.filteredCompanies) {
          let outOfStockChoiceItems = company.outOfStockChoiceItems
            .filter(i => this.$to(i.name).toLowerCase().indexOf(this.choiceItemFilter.toLowerCase()) > -1);
          for (let choiceItem of outOfStockChoiceItems) {
            let foundChoiceItem = choiceItems.find(i => i.id === choiceItem.id);
            if (!foundChoiceItem) {
              choiceItem.companies = [company];
              choiceItems.push(choiceItem);
            } else {
              foundChoiceItem.companies.push(company);
            }
          }
        }
        return choiceItems;
      },
      outOfStockVariants() {
        let variants = [];
        for (let company of this.filteredCompanies) {
          let outOfStockChoiceVariants = company.outOfStockChoiceVariants
            .filter(i => this.$to(i.name).toLowerCase().indexOf(this.variantFilter.toLowerCase()) > -1
              || this.$to(i.parentItem.name).toLowerCase().indexOf(this.variantFilter.toLowerCase()) > -1);
          for (let variant of outOfStockChoiceVariants) {
            let foundVariant = variants.find(i => i.id === variant.id);
            if (!foundVariant) {
              variant.companies = [company];
              variants.push(variant);
            } else {
              foundVariant.companies.push(company);
            }
          }
        }
        return variants;
      }
    },

    methods: {
      async loadCompanies() {
        let response = await Core.request("GET", "/api/branches/" + this.branchId + "/companies");
        this.companies = (response || [])
          .filter(c => c.inventory === this.inventory.id)
          .map(c => ({
            name: c.name,
            nameCanonical: c.nameCanonical,
            outOfStockItems: [],
            outOfStockChoiceVariants: [],
            outOfStockChoiceItems: [],
            outOfStockModifiers: []
          }));
      },
      async loadCompanyInventories() {
        let requests = [];
        for (let company of this.companies) {
          let url = "/api/inventory/" + company.nameCanonical;
          requests = [...requests, ...[
            Core.request("GET", url + "/items"),
            Core.request("GET", url + "/modifiers"),
            Core.request("GET", url + "/choice_items"),
            Core.request("GET", url + "/variants")]];
        }
        let response = await Promise.all(requests);
        for (let i = 0; i < this.companies.length; i++) {
          let company = this.companies[i];
          let offset = i * 4;
          let items = response[offset];
          let modifiers = response[offset + 1];
          let choiceItems = response[offset + 2];
          let variants = response[offset + 3];

          company.outOfStockItems = (items || []).filter(i => i.outOfStock && i.categories.length > 0);
          company.outOfStockModifiers = (modifiers || []).filter(m => m.outOfStock);
          company.outOfStockChoiceItems = (choiceItems || []).filter(i => i.outOfStock);
          company.outOfStockChoiceVariants = (variants || []).filter(v => v.outOfStock);

          for (let variant of company.outOfStockChoiceVariants) {
            variant.parentItem = items.find(i => i.id === variant.parent);
          }
          company.outOfStockChoiceVariants = company.outOfStockChoiceVariants.filter(v => !!v.parentItem);
        }
      },
      downloadItems() {
        let rows = [];
        for (let item of this.outOfStockItems) {
          let companies = item.companies.map(c => removeDiacritics(c.name)).join(" / ");
          let row = [removeDiacritics(this.$to(item.name)), item.externalId, this.$t("items.out_of_stock"), companies, item.companies.length];
          rows.push(row);
        }
        this.downloadAsCsv("items", rows);
      },
      downloadVariants() {
        let rows = [];
        for (let item of this.outOfStockVariants) {
          let companies = item.companies.map(c => removeDiacritics(c.name)).join(" / ");
          let row = [removeDiacritics(this.$to(item.parentItem.name) + " - " + this.$to(item.name)), item.externalId, this.$t("items.out_of_stock"), companies, item.companies.length];
          rows.push(row);
        }
        this.downloadAsCsv("variants", rows);
      },
      downloadChoiceItems() {
        let rows = [];
        for (let item of this.outOfStockChoiceItems) {
          let companies = item.companies.map(c => removeDiacritics(c.name)).join(" / ");
          let row = [removeDiacritics(this.$to(item.name)), item.externalId, this.$t("items.out_of_stock"), companies, item.companies.length];
          rows.push(row);
        }
        this.downloadAsCsv("combo-products", rows);
      },
      downloadModifiers() {
        let rows = [];
        for (let item of this.outOfStockModifiers) {
          let companies = item.companies.map(c => removeDiacritics(c.name)).join(" / ");
          let row = [removeDiacritics(this.$to(item.name)), item.externalId, this.$t("items.out_of_stock"), companies, item.companies.length];
          rows.push(row);
        }
        this.downloadAsCsv("modifiers", rows);
      },
      downloadAsCsv(name, rows) {
        let exportDate = new Date().toISOString().slice(0, 16).replace("T", "_");
        let fileName = this.branch.nameCanonical + "_" + name + "_" + exportDate;
        let rowNames = [this.$t('name'), this.$t('items.external_id'), removeDiacritics(this.$t('disponibility')), this.$t('companies.title'), this.$t('total')];
        rows = [rowNames, ...rows];
        window._downloadAsCsv(fileName, rows);
      }
    }
  }
</script>

<style lang="scss" scoped>
  table {
    width: 100%;

    th {
      font-weight: 500;
      color: #808080;
      font-size: 14px;
    }

    td,
    th {
      padding: 10px;
    }

    tbody tr:nth-child(odd) {
      background-color: #f5f5f5;

      td:first-child {
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }

      td:last-child {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }
    }

    .name {
      text-align: left;
      width: 50%;
    }

    .availability {
      text-align: center;
      width: 10%;
    }

    .companies {
      text-align: right;
      width: 40%;
    }
  }

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: #fc6650;
    }
  }

  .tag {
    text-transform: none;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 2px;
    width: 30%;
    text-align: center;
  }

  .filter-row {
    display: flex;
    align-items: flex-end;

    .group {
      flex-grow: 1;
      margin-right: 10px;
    }

    .action {
      padding-bottom: 10px;

      .btn {
        height: 43px;
      }
    }
  }

  @media screen and (max-width: 900px) {
    .name {
      vertical-align: top;
    }

    .availability {
      display: none;
    }

    .companies {
      width: 50%;
    }

    .tag {
      width: 100%;
    }
  }
</style>